import { useEffect, useRef } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { html, nextTick, reactive, type Reactive } from '@arrow-js/core';
import { MarkdownContent } from './MarkdownContent';
import {
  EMJAE_MASCOT_SIZES,
  SPRITE_PLACEHOLDER,
  SPRITE_SHEET,
  emjaeMascotClass,
  headerPose,
  replyCelebrates,
  replyHasText,
  type EmjaeMascotMotion,
  type EmjaeMascotPose,
  type EmjaeMascotSize,
} from './emjaeMascotMotion';
import {
  awaitingLabel,
  formatElapsed,
  hasSettledTrace,
  toolStatusWord,
  traceSummary,
  type ToolActivityEntry,
} from './chatActivity';
import { toolLabel } from './toolLabels';
import { warmPoseSheets } from './emjaeSprite';
import { welcomeFor } from './chatPagePresets';
import { offeredRoutePath } from './navigation';
import { formatApprovalValue, writeApprovalAfter } from './WriteApprovalCard';
import { TechnicalAuditProposalCard } from './TechnicalAuditProposalCard';
import type { CentralChatContextType, CentralChatMessage, ChatHubConversation } from './types';
import { isAgilePlanApproval, isClientStatusApproval, isNewClientApproval } from './types';
import {
  AGILE_PLAN_DRY_RUN_LINE,
  AGILE_PLAN_GROUP_LABEL,
  AGILE_PLAN_LIST_LABEL,
  agilePlanChangeLine,
  agilePlanExpiry,
  agilePlanResultFor,
  agilePlanResultLine,
  agilePlanRowHeading,
  agilePlanRowLabel,
  agilePlanTeamworkLine,
} from './agilePlanApprovalText';
import {
  CLIENT_STATUS_DRY_RUN_LINE,
  clientStatusGroupLabel,
  clientStatusLine,
  clientStatusSubtitle,
  clientStatusTitle,
} from './clientStatusApprovalText';
import {
  NEW_CLIENT_DRY_RUN_LINE,
  NEW_CLIENT_GROUP_LABEL,
  accessLine,
  blogApprovalLine,
  plannerLine,
  servicesLine,
  setupLine,
  unmatchedLine,
} from './newClientApprovalText';
import type { ChatAccessMode } from './chatAccessMode';

export interface CentralChatMode {
  id: string;
  number: string;
  label: string;
  prompt: string;
}

export interface CentralChatWorkflowTile {
  id: string;
  mode: string;
  modeLabel: string;
  title: string;
  description: string;
  prompt: string;
  contextType?: CentralChatContextType;
  recommended?: boolean;
}

// A type alias, not an interface: arrow-js constrains `reactive()` to
// `Record<PropertyKey, unknown>`, which an interface never satisfies because it
// gets no implicit index signature. As an interface this picked the
// `reactive(effect)` overload instead and every `state.x` read was an error.
type ArrowSurfaceState = {
  mounted: boolean;
  tenantId: string;
  contextLabel: string;
  draft: string;
  status: string;
  isStreaming: boolean;
  conversationLoading: boolean;
  activeConversationId: string;
  chatAccessMode: ChatAccessMode;
  messages: CentralChatMessage[];
  conversations: ChatHubConversation[];
  toolActivity: ToolActivityEntry[];
  // 0 while no turn is live. The ticker below turns it into `elapsedMs`, which
  // is what the trace actually reads, so the clock re-renders one text node
  // rather than the whole surface.
  turnStartedAt: number;
  elapsedMs: number;
  // Which traces the reader has opened or shut, by message id (`live` for the
  // turn in flight). Absent means "however this one opens by default".
  traceOpen: Record<string, boolean>;
  modes: CentralChatMode[];
  workflowTiles: CentralChatWorkflowTile[];
  helpText: string;
  // A finished reply is celebrated (happy) on its avatar until the animation ends.
  happy: boolean;
};

interface CentralChatArrowSurfaceProps {
  tenantId: string | null;
  contextLabel: string;
  draft: string;
  status: string;
  isStreaming: boolean;
  conversationLoading: boolean;
  activeConversationId: string | null;
  chatAccessMode?: ChatAccessMode;
  messages: CentralChatMessage[];
  conversations: ChatHubConversation[];
  toolActivity: ToolActivityEntry[];
  turnStartedAt: number | null;
  modes: CentralChatMode[];
  workflowTiles: CentralChatWorkflowTile[];
  helpText: string;
  onDraftChange: (draft: string) => void;
  onChatAccessModeChange?: (mode: ChatAccessMode) => void;
  onSubmit: (text: string) => void;
  onStop: () => void;
  onWorkflow: (tile: CentralChatWorkflowTile) => void;
  onMode: (mode: CentralChatMode) => void;
  onOpenConversation: (conversationId: string) => void;
  onNewChat: () => void;
  onGoHome: () => void;
  onSandboxOutput: (payload: unknown) => void;
  onSettleWrite?: (decision: 'approve' | 'cancel') => void;
  onAnswerQuestion?: (answer: { optionId: string }) => void;
  className?: string;
}

interface MountedMarkdown {
  content: string;
  root: Root;
  element: Element;
}

interface MountedTaProposal {
  proposalId: string;
  root: Root;
  element: Element;
}

// The same <img> the React panel renders (EmjaeMascot), as an Arrow template:
// same test hooks, same classes, same sizes. `motion` is a thunk so a state
// class (float / sway) follows the reactive state instead of being frozen.
function mascotImg(
  size: EmjaeMascotSize,
  motion: () => (EmjaeMascotMotion | false)[] = () => [],
  pose: () => EmjaeMascotPose = () => 'idle',
  on: { iteration?: () => void; end?: () => void } = {},
  /** Avatars only: true for the avatar beside the reply being generated, which plays the 64 sprite. */
  live: () => boolean = () => false,
) {
  const { px, src } = EMJAE_MASCOT_SIZES[size];
  const hero = size === 'hero';
  // The header and hero play the badger sprite (same classes as the panel; no sheet probing
  // here, this surface is minimal). Avatars are the still image, except the live one.
  const animated = () => (size === 'avatar' ? live() : SPRITE_SHEET[size] !== undefined);
  const shownPose = () => (size === 'avatar' && !live() ? 'idle' : pose());
  return html`<img
    src="${() => (animated() ? SPRITE_PLACEHOLDER : src)}"
    width="${px}"
    height="${px}"
    draggable="false"
    data-testid="emjae-mascot"
    data-size="${size}"
    data-pose="${() => shownPose()}"
    alt="${hero ? 'Emjae' : ''}"
    aria-hidden="${hero ? 'false' : 'true'}"
    class="${() =>
      emjaeMascotClass(
        size,
        motion(),
        animated() ? { pose: pose(), sheet: size === 'avatar' ? '64' : undefined } : undefined,
      )}"
    @animationiteration="${() => on.iteration?.()}"
    @animationend="${() => on.end?.()}"
  />`;
}


function tenantDomain(tenantId: string): string {
  if (!tenantId) return 'no workspace';
  return tenantId.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

function relativeTime(value?: string | null): string {
  if (!value) return '—';
  const ms = Date.now() - new Date(value).getTime();
  if (Number.isNaN(ms)) return '—';
  const min = 60_000;
  const hour = 60 * min;
  const day = 24 * hour;
  if (ms < min) return 'now';
  if (ms < hour) return `${Math.floor(ms / min)}m ago`;
  if (ms < day) return `${Math.floor(ms / hour)}h ago`;
  if (ms < 7 * day) return `${Math.floor(ms / day)}d ago`;
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// The chat is "working" whenever a turn is in flight — while the runtime is
// preparing or refreshing page context (which happens BEFORE the stream opens,
// so isStreaming is still false), and once the stream is live. Both surface as
// the top banner so the reader sees progress at the top of the thread, not
// buried at the composer where Elijah missed it.
function surfaceWorking(state: ArrowSurfaceState): boolean {
  if (state.isStreaming) return true;
  return /page context/i.test(state.status);
}

function surfaceBannerLabel(state: ArrowSurfaceState): string {
  return state.status || "I'm working…";
}

function composerStatusLabel(state: ArrowSurfaceState): string {
  // The top banner now carries the working status, so the composer line drops
  // its duplicate text and keeps only the dot while a turn runs.
  if (surfaceWorking(state)) return '';
  if (state.status && /error/i.test(state.status)) return state.status;
  if (!state.tenantId) return 'Select a workspace first';
  return state.status || 'Ready';
}

function composerStatusDotClass(state: ArrowSurfaceState): string {
  if (state.isStreaming) return 'size-1.5 rounded-full bg-teal animate-pulse';
  if (state.status && /error/i.test(state.status)) return 'size-1.5 rounded-full bg-destructive';
  return 'size-1.5 rounded-full bg-ink-3';
}

function textareaValue(event: Event) {
  return event.target instanceof HTMLTextAreaElement ? event.target.value : '';
}

export function CentralChatArrowSurface({
  tenantId,
  contextLabel,
  draft,
  status,
  isStreaming,
  conversationLoading,
  activeConversationId,
  chatAccessMode = 'ask',
  messages,
  conversations,
  toolActivity,
  turnStartedAt,
  modes,
  workflowTiles,
  helpText,
  onDraftChange,
  onChatAccessModeChange,
  onSubmit,
  onStop,
  onWorkflow,
  onMode,
  onOpenConversation,
  onNewChat,
  onGoHome,
  onSandboxOutput,
  onSettleWrite,
  onAnswerQuestion,
  className,
}: CentralChatArrowSurfaceProps) {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const stateRef = useRef<Reactive<ArrowSurfaceState> | null>(null);
  const mountedMarkdownRef = useRef<Map<string, MountedMarkdown>>(new Map());
  const mountedTaProposalRef = useRef<Map<string, MountedTaProposal>>(new Map());
  const callbacksRef = useRef({
    onDraftChange,
    onChatAccessModeChange,
    onSubmit,
    onStop,
    onWorkflow,
    onMode,
    onOpenConversation,
    onNewChat,
    onGoHome,
    onSandboxOutput,
    onSettleWrite,
    onAnswerQuestion,
  });

  callbacksRef.current = {
    onDraftChange,
    onChatAccessModeChange,
    onSubmit,
    onStop,
    onWorkflow,
    onMode,
    onOpenConversation,
    onNewChat,
    onGoHome,
    onSandboxOutput,
    onSettleWrite,
    onAnswerQuestion,
  };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // The composer mascot plays the non-idle sheets here too (no React mascot on /chat to start the warm-up).
    warmPoseSheets();

    const state = reactive<ArrowSurfaceState>({
      mounted: true,
      tenantId: tenantId || '',
      contextLabel,
      draft,
      status,
      isStreaming,
      conversationLoading,
      activeConversationId: activeConversationId || '',
      chatAccessMode,
      messages,
      conversations,
      toolActivity,
      turnStartedAt: turnStartedAt ?? 0,
      elapsedMs: 0,
      traceOpen: {},
      modes,
      workflowTiles,
      helpText,
      happy: false,
    });
    stateRef.current = state;

    // The one animated avatar: the last message when it is an assistant reply being generated (thinking
    // until the first text or while a tool runs, then talking), then happy once when it finished well.
    const isLiveAvatar = (id: string) => {
      const last = state.messages[state.messages.length - 1];
      return (state.isStreaming || state.happy) && last?.role === 'assistant' && last.id === id;
    };
    const liveAvatarPose = (id: string): EmjaeMascotPose =>
      state.isStreaming
        ? headerPose({
            isStreaming: true,
            toolRunning: state.toolActivity.some((entry) => entry.status === 'running'),
            replyStarted: replyHasText(state.messages.filter((item) => item.id === id)),
            happy: false,
            wave: false,
          })
        : 'happy';

    const onAvatarEnd = () => {
      state.happy = false;
    };

    // An approved Agile Plan card is settling (or streaming is on): its buttons are disabled and do nothing.
    const isSettling = (id: string) => state.isStreaming || state.messages.find((item) => item.id === id)?.settling === true;

    const submitDraft = () => {
      const text = state.draft.trim();
      if (!text || state.isStreaming || !state.tenantId) return;
      callbacksRef.current.onSubmit(text);
    };

    const updateDraft = (value: string) => {
      state.draft = value;
      callbacksRef.current.onDraftChange(value);
    };

    const toggleTrace = (key: string) => {
      const open = state.traceOpen[key] ?? false;
      state.traceOpen = { ...state.traceOpen, [key]: !open };
    };

    /**
     * A turn as a trace: the house thinking mark, a label that shimmers while
     * the work is live and states the duration once it is not, a wall clock
     * beside it, and the tools underneath as rows that resolve.
     *
     * Mirrors EmjaeTrace.tsx in the React bubble. The grammar itself lives once
     * in index.css (`.emjae-trace*`), so only this markup is duplicated across
     * the two surfaces — the same trade `.emjae-thinking` already makes. Change
     * one, change both.
     */
    const traceView = (
      key: string,
      working: boolean,
      entries: () => ToolActivityEntry[],
      label: () => string,
    ) => {
      const panelId = `emjae-trace-${key}`;
      const isOpen = () => (state.traceOpen[key] ?? false) && entries().length > 0;
      return html`
        <div
          class="emjae-trace flex w-full flex-col"
          data-emjae-trace="true"
          data-working="${working ? 'true' : 'false'}"
          data-rows="${() => (entries().length > 0 ? 'true' : 'false')}"
        >
          <button
            type="button"
            class="emjae-trace-toggle -mx-1.5 flex w-fit items-center gap-2 rounded-control px-1.5 py-1 text-left transition-colors hover:bg-paper-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
            aria-expanded="${() => (isOpen() ? 'true' : 'false')}"
            aria-controls="${panelId}"
            @click="${() => toggleTrace(key)}"
          >
            <span class="emjae-thinking" aria-hidden="true">
              <span class="emjae-thinking-field"></span>
              <span class="emjae-thinking-catch" style="--x: 13px; --d: 0.62s"></span>
              <span class="emjae-thinking-catch" style="--x: 28px; --d: 1.38s"></span>
              <span class="emjae-thinking-scan"></span>
            </span>
            <span
              role="status"
              class="${working ? 'emjae-shimmer text-xs font-medium' : 'text-xs font-medium text-ink-2'}"
            >${() => label()}</span>
            ${working
              ? html`<span class="font-mono text-xs text-ink-3 tabular-nums" aria-hidden="true">${() => formatElapsed(state.elapsedMs)}</span>`
              : null}
            ${() => entries().length > 0
              ? html`<svg class="emjae-trace-chevron size-3 text-ink-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"></path></svg>`
              : null}
          </button>

          <div
            id="${panelId}"
            class="emjae-trace-panel"
            data-open="${() => (isOpen() ? 'true' : 'false')}"
          >
            <div>
              <ol class="relative mt-1 ml-1.5 flex flex-col gap-0.5 pl-3">
                <span class="emjae-trace-spine" aria-hidden="true"></span>
                ${() => entries().map((entry, index) => html`
                  <li
                    class="emjae-trace-row flex min-h-6 items-center gap-2 pr-1"
                    style="${`--i: ${index}`}"
                    data-emjae-trace-row="true"
                    title="${entry.name}"
                  >
                    <span class="emjae-trace-mark" data-status="${entry.status}" aria-hidden="true"></span>
                    <span class="min-w-0 truncate text-xs text-ink-2">${toolLabel(entry.name)}</span>
                    ${entry.detail
                      ? html`<span class="min-w-0 shrink truncate text-xs text-ink-3" title="${entry.detail}">${entry.detail}</span>`
                      : null}
                    <span class="sr-only">${toolStatusWord(entry.status)}</span>
                  </li>
                `.key(`${entry.name}-${index}`))}
              </ol>
            </div>
          </div>
        </div>
      `;
    };

    const view = html`${() => state.mounted ? html`
      <div class="h-[calc(100vh-4rem)] flex flex-col bg-warm-bg text-ink overflow-hidden">
        <header class="h-16 shrink-0 flex items-center gap-4 border-b border-warm-border bg-warm-bg px-6">
          <div class="min-w-0 flex flex-col">
            <h1 class="font-display text-[20px] leading-none tracking-[-0.01em] text-ink">Emjae</h1>
            <p class="mt-1 font-mono text-[11px] tracking-wider uppercase text-ink-3 truncate">
              ${() => `${tenantDomain(state.tenantId)} · ${state.contextLabel}`}
            </p>
          </div>
          <div class="flex-1"></div>
          <button
            type="button"
            disabled
            title="Cmd-K palette is coming soon"
            class="hidden md:inline-flex items-center gap-2 rounded-full border border-warm-border bg-paper px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-ink-3 hover:border-warm-border-strong hover:text-ink-2 transition-colors"
          >
            <span class="opacity-70">search</span>
            <kbd class="rounded border border-warm-border bg-warm-bg px-1.5 py-0.5 text-[10px] text-ink-2">⌘ K</kbd>
          </button>
          <button
            type="button"
            @click="${() => callbacksRef.current.onGoHome()}"
            class="inline-flex items-center gap-1.5 rounded-full border border-warm-border bg-transparent px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-ink-2 hover:border-warm-border-strong hover:text-ink hover:bg-paper-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
          >
            home
            <span aria-hidden="true">→</span>
          </button>
        </header>

        <div class="flex-1 grid min-h-0 xl:grid-cols-[250px_minmax(0,1fr)]">
          <aside class="hidden xl:flex min-h-0 flex-col border-r border-warm-border bg-warm-bg">
            <div class="px-4 pt-5">
              <button
                type="button"
                @click="${() => callbacksRef.current.onNewChat()}"
                class="w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 font-mono text-[11px] uppercase tracking-wider font-semibold hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/15"
              >
                <span aria-hidden="true">+</span>
                new chat
              </button>
            </div>
            <div class="px-4 pt-6 pb-2 flex items-baseline gap-2">
              <span class="font-mono text-[10px] uppercase tracking-wider text-ink-3">01 · conversations</span>
              ${() => state.conversationLoading
                ? html`<span class="font-mono text-[10px] uppercase tracking-wider text-ink-3 animate-pulse">loading</span>`
                : null}
            </div>
            <div class="relative flex-1 min-h-0">
              <div class="pointer-events-none absolute inset-x-0 top-0 h-4 z-10 bg-gradient-to-b from-warm-bg to-transparent" aria-hidden="true"></div>
              <div class="pointer-events-none absolute inset-x-0 bottom-0 h-4 z-10 bg-gradient-to-t from-warm-bg to-transparent" aria-hidden="true"></div>
              <div class="h-full overflow-y-auto px-2 pb-4 space-y-0.5">
              ${() => state.conversations.length === 0 && !state.conversationLoading
                ? html`<div class="px-3 py-4 font-mono text-[11px] text-ink-3 italic">no conversations yet</div>`
                : null}
              ${() => state.conversations.map((conversation, index) => html`
                <button
                  type="button"
                  @click="${() => callbacksRef.current.onOpenConversation(conversation.id)}"
                  title="${conversation.title || 'Central chat'}"
                  class="${() => {
                    const active = conversation.id === state.activeConversationId;
                    return `group w-full flex items-baseline gap-2.5 rounded-md py-2 pr-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 ${
                      active
                        ? 'bg-paper-2 border-l-[3px] border-l-coral pl-[calc(0.625rem-3px)]'
                        : 'border-l-[3px] border-l-transparent pl-[calc(0.625rem-3px)] hover:bg-paper'
                    }`;
                  }}"
                >
                  <span class="font-mono text-[10px] tabular-nums text-ink-3 w-4 shrink-0">
                    ${String(index + 1).padStart(2, '0')}
                  </span>
                  <span class="${() => {
                    const active = conversation.id === state.activeConversationId;
                    return `min-w-0 flex-1 text-[13px] leading-snug truncate ${
                      active ? 'font-semibold text-ink' : 'text-ink-2 group-hover:text-ink'
                    }`;
                  }}">
                    ${conversation.title || 'Central chat'}
                  </span>
                  <span class="font-mono text-[10px] tabular-nums text-ink-3 shrink-0">
                    ${relativeTime(conversation.last_message_at || conversation.updated_at)}
                  </span>
                </button>
              `.key(conversation.id))}
              </div>
            </div>
          </aside>

          <section class="flex min-h-0 flex-col overflow-hidden">
            ${() => surfaceWorking(state)
              ? html`<div
                  data-mj-fetch-banner="true"
                  role="status"
                  aria-live="polite"
                  class="shrink-0 flex items-center gap-2.5 border-b border-warm-border bg-coral-light px-6 py-2.5"
                >
                  <span class="emjae-thinking" aria-hidden="true">
                    <span class="emjae-thinking-field"></span>
                    <span class="emjae-thinking-catch" style="--x: 13px; --d: 0.62s"></span>
                    <span class="emjae-thinking-catch" style="--x: 28px; --d: 1.38s"></span>
                    <span class="emjae-thinking-scan"></span>
                  </span>
                  <span class="emjae-shimmer text-xs font-medium tracking-wide">${() => surfaceBannerLabel(state)}</span>
                </div>`
              : null}
            <div class="flex-1 overflow-y-auto">
              ${() => state.messages.length === 0
                ? html`
                  <div class="w-full px-6 pt-16 pb-10 flex flex-col">
                    <div class="mb-6 flex">${mascotImg('hero', () => ['float'])}</div>
                    <div>
                      <div class="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-wider text-teal-dark">
                        <span class="size-1.5 rounded-full bg-teal animate-pulse" aria-hidden="true"></span>
                        central chat
                      </div>
                      <h2 class="mt-4 font-display text-[40px] leading-[1.05] tracking-[-0.02em] text-ink">
                        Plan, inspect, generate, or <span class="text-coral">navigate</span>.
                      </h2>
                      <p class="mt-4 max-w-xl text-[15px] leading-relaxed text-ink-2">
                        The chat keeps route context attached so the answer stays grounded in this workspace.
                      </p>
                    </div>

                    <p data-testid="chat-welcome-line" class="mt-6 max-w-xl text-[15px] leading-snug text-ink-2">${welcomeFor('/chat')}</p>

                    <div class="mt-8 flex flex-wrap items-center gap-2">
                      ${() => state.modes.map((mode) => html`
                        <button
                          type="button"
                          @click="${() => callbacksRef.current.onMode(mode)}"
                          disabled="${() => !state.tenantId}"
                          class="inline-flex items-center gap-2 rounded-full border border-warm-border bg-paper px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-ink-2 hover:bg-paper-2 hover:border-warm-border-strong hover:text-ink transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                        >
                          <span class="font-semibold text-[10px] text-ink-3">${mode.number}</span>
                          ${mode.label}
                        </button>
                      `.key(mode.id))}
                    </div>

                    <a
                      href="#central-chat-workflows"
                      class="mt-6 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-teal-dark hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40 rounded-sm self-start"
                    >
                      see workflows
                      <span aria-hidden="true">↓</span>
                    </a>

                    <div id="central-chat-workflows" class="mt-12 pt-8 border-t border-warm-border scroll-mt-20">
                      <div class="mb-5 flex items-baseline gap-3">
                        <span class="font-mono text-[10px] uppercase tracking-wider text-ink-3">02 · workflows</span>
                        <span class="text-[13px] text-ink-2">Pick a workflow to start</span>
                      </div>
                      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                        ${() => state.workflowTiles.map((tile) => html`
                          <button
                            type="button"
                            @click="${() => callbacksRef.current.onWorkflow(tile)}"
                            disabled="${() => !state.tenantId || state.isStreaming}"
                            aria-label="${tile.recommended ? `Recommended: ${tile.title}` : tile.title}"
                            class="${() =>
                              `group rounded-xl border bg-paper text-left transition-all hover:bg-paper-2 hover:border-warm-border-strong hover:shadow-soft disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-none focus-visible:border-coral focus-visible:ring-[3px] focus-visible:ring-coral/15 ${
                                tile.recommended
                                  ? 'border-warm-border border-l-[3px] border-l-coral pl-[calc(1.25rem-3px)] pr-5 py-5'
                                  : 'border-warm-border p-5'
                              }`
                            }"
                          >
                            <div class="flex items-baseline gap-2 font-mono text-[10px] uppercase tracking-wider text-ink-3">
                              <span class="font-semibold">${tile.mode}</span>
                              <span>${tile.modeLabel}</span>
                              ${tile.recommended
                                ? html`<span class="ml-auto text-coral">recommended</span>`
                                : null}
                            </div>
                            <h3 class="mt-2.5 text-[18px] leading-snug text-ink">${tile.title}</h3>
                            <p class="mt-1.5 text-[13px] leading-relaxed text-ink-2 line-clamp-2">${tile.description}</p>
                            <span class="mt-3 inline-flex font-mono text-[11px] uppercase tracking-wider text-ink-3 group-hover:text-teal-dark transition-colors">
                              open →
                            </span>
                          </button>
                        `.key(tile.id))}
                      </div>
                    </div>

                    <div class="mt-12 font-mono text-[10px] uppercase tracking-wider text-ink-3">
                      ${() => state.helpText}
                    </div>
                  </div>
                `
                : html`
                  <div role="log" aria-live="polite" aria-relevant="additions text" class="w-full px-6 py-10 flex flex-col gap-2">
                    ${() => state.messages.map((message, index, arr) => {
                      const isLast = index === arr.length - 1;
                      return html`
                        <article class="${() => `mt-6 flex gap-2.5 first:mt-0 ${message.role === 'user' ? 'flex-col items-end' : 'flex-row items-start'}`}" aria-busy="${() => state.isStreaming && isLast ? 'true' : 'false'}">
                          ${message.role === 'assistant' ? mascotImg('avatar', () => [], () => liveAvatarPose(message.id), { end: onAvatarEnd }, () => isLiveAvatar(message.id)) : null}
                          <div class="${() =>
                            `${message.role === 'user'
                              ? 'max-w-[65%] rounded-xl bg-coral-light text-coral-dark px-4 py-3 text-[15px] leading-relaxed'
                              : 'min-w-0 flex-1 rounded-xl border border-warm-border bg-paper text-ink px-5 py-4 hover:shadow-soft transition-shadow'
                            }`
                          }">
                            ${() => {
                              // The live run. Read only `state.isStreaming` here:
                              // the rows and the label are thunks inside the
                              // trace, so a tool landing repaints a row instead
                              // of rebuilding the whole block under the reader.
                              if (message.role !== 'assistant' || !isLast || !state.isStreaming) return null;
                              return html`<div class="w-full mb-1.5">${traceView(
                                'live',
                                true,
                                () => state.toolActivity,
                                () => state.status || 'Reading\u2026',
                              )}</div>`;
                            }}
                            ${() => {
                              // The run behind a finished answer, kept on the
                              // message so the next turn does not erase it.
                              if (message.role !== 'assistant') return null;
                              if (isLast && state.isStreaming) return null;
                              const current = state.messages.find((item) => item.id === message.id);
                              if (!current || !hasSettledTrace(current)) return null;
                              const rows = current.trace ?? [];
                              return html`<div class="w-full mb-1.5">${traceView(
                                message.id,
                                false,
                                () => rows,
                                () => traceSummary(
                                  current.traceOutcome ?? 'done',
                                  rows.length,
                                  current.traceDurationMs ?? null,
                                  current.awaiting,
                                ),
                              )}</div>`;
                            }}
                            ${() => {
                              const current = state.messages.find((item) => item.id === message.id);
                              const content = current?.content || '';
                              const lastMessage = state.messages[state.messages.length - 1];
                              const live = state.isStreaming && lastMessage?.id === message.id;
                              if (message.role === 'user') {
                                // Pre-wrap belongs on the text, not on the card:
                                // the card also contains this template's own
                                // indentation as text nodes, and pre-wrap turned
                                // every one of them into a blank line.
                                return html`<span class="whitespace-pre-wrap">${content}</span>`;
                              }
                              // The trace above this now carries the thinking
                              // mark, the live status line and the clock, so an
                              // empty assistant message during a live turn adds
                              // nothing here. What is left is the case the
                              // trace cannot cover: a turn that ended having
                              // said nothing at all.
                              //
                              // The blinking caret that used to sit after the
                              // text is gone with it. Emjae answers whole — the
                              // caret sat beside text that never grew, which
                              // made it a claim about streaming this chat has
                              // never done.
                              if (!content) {
                                return live
                                  ? null
                                  : html`<span class="text-ink-3 italic text-[15px]">No response.</span>`;
                              }
                              return html`<div data-central-chat-message-md="${message.id}"></div>`;
                            }}
                            ${() => {
                              const current = state.messages.find((item) => item.id === message.id);
                              return current?.taProposal
                                ? html`<div class="mt-3" data-central-chat-ta-proposal="${message.id}"></div>`
                                : null;
                            }}
                            ${() => {
                              const current = state.messages.find((item) => item.id === message.id);
                              return current?.error
                                ? html`<div role="alert" class="mt-3 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 font-mono text-[11px] tracking-wide text-destructive">${current.error}</div>`
                                : null;
                            }}
                            ${() => {
                              const current = state.messages.find((item) => item.id === message.id);
                              if (current?.writeApproval) {
                                const card = current.writeApproval;
                                if ((isClientStatusApproval(card) || isNewClientApproval(card)) && card.declineOnly) {
                                  // Nothing to approve: never a card. An old stored park shows its notice as plain text.
                                  return card.notice ? html`<p class="mt-3 text-[12px] text-ink">${card.notice}</p>` : null;
                                }
                                if (isAgilePlanApproval(card)) {
                                  // The same facts as the React card (shared text). Settled (results given): no buttons.
                                  const settled = current.agilePlanResults !== undefined;
                                  const expiry = agilePlanExpiry(card, new Date());
                                  return html`<div role="group" aria-label="${AGILE_PLAN_GROUP_LABEL}" class="mt-3 rounded-lg border border-warm-border bg-paper px-3 py-2.5">
                                    ${card.dryRun ? html`<p role="status" class="mb-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[11px] text-ink-2">${AGILE_PLAN_DRY_RUN_LINE}</p>` : null}
                                    <ul role="list" aria-label="${AGILE_PLAN_LIST_LABEL}" class="grid gap-2">
                                      ${card.rows.map((row, position) => {
                                        const teamwork = agilePlanTeamworkLine(row);
                                        const result = agilePlanResultFor(current.agilePlanResults, row.index);
                                        return html`<li aria-label="${agilePlanRowLabel(position, card.rows.length)}" class="rounded-md border border-warm-border bg-paper-2 px-2.5 py-2">
                                          <p class="font-display text-[14px] leading-tight text-ink">${agilePlanRowHeading(row)}</p>
                                          ${row.valid ? row.changes.map((change) => html`<p class="mt-1 text-[12px] text-ink-2">${agilePlanChangeLine(change)}</p>`) : null}
                                          ${row.valid && row.diff ? html`<p class="mt-1 whitespace-pre-wrap text-[12px] text-ink-2">${row.diff.removed ? html`<del class="text-destructive">${row.diff.removed}</del>` : null}${row.diff.removed && row.diff.added ? ' ' : null}${row.diff.added ? html`<ins class="text-ink no-underline">${row.diff.added}</ins>` : null}</p>` : null}
                                          ${teamwork ? html`<p class="mt-1 font-mono text-[11px] text-ink-3">${teamwork}</p>` : null}
                                          ${row.valid && row.noop ? html`<p class="mt-1 text-[12px] text-ink-3">No change</p>` : null}
                                          ${row.valid ? null : html`${row.errors.map((error) => html`<p class="mt-1 text-[12px] text-destructive">${error}</p>`)}<p class="mt-1 font-mono text-[11px] text-ink-3">Will not be applied</p>`}
                                          ${result ? html`<p class="mt-1 text-[12px] font-medium text-ink">${agilePlanResultLine(result)}</p>` : null}
                                        </li>`;
                                      })}
                                    </ul>
                                    ${settled ? null : html`<p class="mt-2 font-mono text-[11px] text-ink-3">${expiry.text}</p>`}
                                    ${card.notice ? html`<p class="mt-2 text-[12px] text-ink">${card.notice}</p>` : null}
                                    ${current.agilePlanResultText ? html`<p class="mt-2 whitespace-pre-wrap text-[12px] text-ink">${current.agilePlanResultText}</p>` : null}
                                    ${settled ? null : html`<div class="mt-3 flex gap-2">
                                      ${card.declineOnly || expiry.expired ? null : html`<button type="button" class="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40" disabled="${() => isSettling(message.id)}" @click="${() => { if (!isSettling(message.id)) callbacksRef.current.onSettleWrite?.('approve'); }}">Approve</button>`}
                                      <button type="button" class="rounded-md border border-warm-border bg-paper px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40" disabled="${() => isSettling(message.id)}" @click="${() => { if (!isSettling(message.id)) callbacksRef.current.onSettleWrite?.('cancel'); }}">Cancel</button>
                                    </div>`}
                                  </div>`;
                                }
                                if (isClientStatusApproval(card)) {
                                  // The same facts as the React card (shared text). No Approve when it is decline-only.
                                  const subtitle = clientStatusSubtitle(card);
                                  return html`<div role="group" aria-label="${clientStatusGroupLabel(card)}" class="mt-3 rounded-lg border border-warm-border bg-paper px-3 py-2.5">
                                    ${card.dryRun ? html`<p role="status" class="mb-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[11px] text-ink-2">${CLIENT_STATUS_DRY_RUN_LINE}</p>` : null}
                                    <p class="font-display text-[15px] leading-tight text-ink">${clientStatusTitle(card)}</p>
                                    ${subtitle ? html`<p class="mt-0.5 font-mono text-[11px] text-ink-2">${subtitle}</p>` : null}
                                    <p class="mt-2 text-[12px] text-ink-2">${clientStatusLine(card)}</p>
                                    ${card.notice ? html`<p class="mt-2 text-[12px] text-ink">${card.notice}</p>` : null}
                                    <div class="mt-3 flex gap-2">
                                      ${card.declineOnly ? null : html`<button type="button" class="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40" disabled="${() => state.isStreaming}" @click="${() => callbacksRef.current.onSettleWrite?.('approve')}">Approve</button>`}
                                      <button type="button" class="rounded-md border border-warm-border bg-paper-2 px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40" disabled="${() => state.isStreaming}" @click="${() => callbacksRef.current.onSettleWrite?.('cancel')}">Decline</button>
                                    </div>
                                  </div>`;
                                }
                                if (isNewClientApproval(card)) {
                                  // The same facts as the React card (shared text), as a template. No
                                  // Approve at all when the card is decline-only.
                                  const unmatched = unmatchedLine(card.unmatchedMemberCount);
                                  return html`<div role="group" aria-label="${NEW_CLIENT_GROUP_LABEL}" class="mt-3 rounded-lg border border-warm-border bg-paper px-3 py-2.5">
                                    ${card.dryRun ? html`<p role="status" class="mb-2 rounded-md border border-warm-border bg-paper-2 px-2 py-1 font-mono text-[11px] text-ink-2">${NEW_CLIENT_DRY_RUN_LINE}</p>` : null}
                                    <p class="font-display text-[15px] leading-tight text-ink">${card.client.name}</p>
                                    <p class="mt-0.5 font-mono text-[11px] text-ink-2">${card.client.website}</p>
                                    <ul class="mt-2 grid gap-1 text-[12px] text-ink-2">
                                      <li>${servicesLine(card.client.services)}</li>
                                      <li>${blogApprovalLine(card.client.approvalRequired)}</li>
                                      <li>${accessLine(card.members, card.unmatchedMemberCount)}</li>
                                      ${unmatched ? html`<li>${unmatched}</li>` : null}
                                      <li>${setupLine(card.preview.willCreate, card.dryRun)}</li>
                                      <li>${plannerLine(card.dryRun)}</li>
                                    </ul>
                                    ${card.notice ? html`<p class="mt-2 text-[12px] text-ink">${card.notice}</p>` : null}
                                    <div class="mt-3 flex gap-2">
                                      ${card.declineOnly ? null : html`<button type="button" class="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40" disabled="${() => state.isStreaming}" @click="${() => callbacksRef.current.onSettleWrite?.('approve')}">Approve</button>`}
                                      <button type="button" class="rounded-md border border-warm-border bg-paper-2 px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40" disabled="${() => state.isStreaming}" @click="${() => callbacksRef.current.onSettleWrite?.('cancel')}">Decline</button>
                                    </div>
                                  </div>`;
                                }
                                return html`<div role="group" aria-label="Approve or decline this write" class="mt-3 rounded-lg border border-warm-border bg-paper px-3 py-2.5">
                                  <dl class="grid gap-1.5 font-mono text-[11px] text-ink-2">
                                    <div><dt class="text-[10px] uppercase tracking-wider text-ink-3">Tool</dt><dd class="text-ink">${card.toolName}</dd></div>
                                    <div><dt class="text-[10px] uppercase tracking-wider text-ink-3">Post</dt><dd class="text-ink">${card.postId == null || card.postId === '' ? 'new draft' : String(card.postId)}</dd></div>
                                    <div><dt class="text-[10px] uppercase tracking-wider text-ink-3">Field</dt><dd class="text-ink">${card.field}</dd></div>
                                    <div><dt class="text-[10px] uppercase tracking-wider text-ink-3">Before</dt><dd class="whitespace-pre-wrap text-ink">${formatApprovalValue(card.before)}</dd></div>
                                    <div><dt class="text-[10px] uppercase tracking-wider text-ink-3">After</dt><dd class="whitespace-pre-wrap text-ink">${formatApprovalValue(writeApprovalAfter(card))}</dd></div>
                                  </dl>
                                  <div class="mt-3 flex gap-2">
                                    <button type="button" class="rounded-md bg-coral px-2.5 py-1 text-[11px] font-medium text-white hover:bg-coral-hover disabled:opacity-50" disabled="${() => state.isStreaming}" @click="${() => callbacksRef.current.onSettleWrite?.('approve')}">Approve</button>
                                    <button type="button" class="rounded-md border border-warm-border bg-paper-2 px-2.5 py-1 text-[11px] font-medium text-ink-2 hover:border-warm-border-strong disabled:opacity-50" disabled="${() => state.isStreaming}" @click="${() => callbacksRef.current.onSettleWrite?.('cancel')}">Decline</button>
                                  </div>
                                </div>`;
                              }
                              return current?.awaiting && !current.taProposal
                                ? html`<div role="status" class="mt-3 rounded-md border border-warm-border bg-paper-2 px-3 py-2 font-mono text-[11px] tracking-wide text-ink-2">${awaitingLabel(current.awaiting)}</div>`
                                : null;
                            }}
                            ${() => {
                              // Suggested next steps. The floating bubble has
                              // rendered these since the proposal flow shipped;
                              // this surface never did, so the same reply
                              // silently offered nothing here. Only on the last
                              // message, and never mid-stream.
                              const current = state.messages.find((item) => item.id === message.id);
                              const last = state.messages[state.messages.length - 1];
                              const replies = current?.quickReplies ?? [];
                              if (!replies.length || current?.id !== last?.id || state.isStreaming) {
                                return null;
                              }
                              return html`<div class="mt-3 flex flex-wrap gap-2" role="group" aria-label="Suggested next steps">
                                ${replies.map((reply) => html`<button
                                  type="button"
                                  @click="${() => callbacksRef.current.onSubmit(reply)}"
                                  class="rounded-full border border-warm-border bg-paper-2 px-3 py-1.5 text-[13px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                                >${reply}</button>`)}
                              </div>`;
                            }}
                            ${() => {
                              // Emjae's own question: one button per choice, on the last message only, never mid-stream.
                              const current = state.messages.find((item) => item.id === message.id);
                              const last = state.messages[state.messages.length - 1];
                              const options = current?.question?.options ?? [];
                              if (!options.length || current?.id !== last?.id || state.isStreaming) return null;
                              return html`<div class="mt-3 flex flex-wrap gap-2" role="group" aria-label="Choices">
                                ${options.map((option) => html`<button
                                  type="button"
                                  title="${option.description ?? ''}"
                                  @click="${() => callbacksRef.current.onAnswerQuestion?.({ optionId: option.id })}"
                                  class="rounded-full border border-warm-border bg-paper-2 px-3 py-1.5 text-left text-[13px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                                >${option.label}${option.description ? html`<span class="block text-[12px] text-ink-3">${option.description}</span>` : null}</button>`)}
                              </div>`;
                            }}
                            ${() => {
                              const current = state.messages.find((item) => item.id === message.id);
                              const buttons = [];
                              for (const action of current?.actions ?? []) {
                                const path = offeredRoutePath(action);
                                if (!path) continue;
                                buttons.push(html`<button
                                  type="button"
                                  @click="${() => callbacksRef.current.onSandboxOutput({ action: 'navigate', path })}"
                                  class="rounded-full border border-warm-border bg-paper-2 px-3 py-1.5 text-[13px] text-ink transition-colors hover:border-coral hover:bg-coral-light hover:text-coral-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
                                >${action.label}</button>`);
                              }
                              return buttons.length
                                ? html`<div class="mt-3 flex flex-wrap gap-2" role="group" aria-label="Suggested destinations">${buttons}</div>`
                                : null;
                            }}
                          </div>
                        </article>
                      `.key(message.id);
                    })}
                    <div data-central-chat-scroll-anchor></div>
                  </div>
                `}
            </div>

            <div class="shrink-0 border-t border-warm-border bg-warm-bg px-6 py-4">
              <div class="w-full">
                <form
                  @submit="${(event: Event) => {
                    event.preventDefault();
                    submitDraft();
                  }}"
                  class="${() => {
                    const errored = state.status && /error/i.test(state.status);
                    return `rounded-xl border bg-paper transition-all duration-150 focus-within:shadow-soft ${
                      errored
                        ? 'border-destructive/30 focus-within:border-destructive focus-within:ring-[3px] focus-within:ring-destructive/15'
                        : 'border-warm-border focus-within:border-coral focus-within:ring-[3px] focus-within:ring-coral/15'
                    }`;
                  }}"
                >
                  <textarea
                    aria-label="Message"
                    .value="${() => state.draft}"
                    @input="${(event: Event) => updateDraft(textareaValue(event))}"
                    @keydown="${(event: Event) => {
                      const keyboardEvent = event as KeyboardEvent;
                      if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
                        event.preventDefault();
                        submitDraft();
                      }
                    }}"
                    disabled="${() => !state.tenantId || state.isStreaming}"
                    placeholder="${() => state.tenantId ? 'Ask the agent to plan, inspect, generate, or navigate…' : 'Select a workspace first'}"
                    rows="3"
                    class="block w-full resize-none border-0 bg-transparent px-4 pt-4 pb-2 text-[15px] leading-relaxed text-ink outline-none placeholder:text-ink-3 disabled:opacity-60"
                  ></textarea>
                  <div class="flex items-center justify-between gap-3 px-4 pb-3 pt-1">
                    <div class="flex items-center gap-2 min-w-0">
                      <label class="inline-flex shrink-0 items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-ink-3">
                        <span>Access</span>
                        <select
                          aria-label="Chat access mode"
                          .value="${() => state.chatAccessMode}"
                          disabled="${() => !state.tenantId}"
                          @change="${(event: Event) => {
                            const target = event.target;
                            if (target instanceof HTMLSelectElement) {
                              callbacksRef.current.onChatAccessModeChange?.(
                                target.value === 'full_access' ? 'full_access' : 'ask',
                              );
                            }
                          }}"
                          class="rounded-md border border-warm-border bg-paper px-1.5 py-1 text-[11px] font-semibold text-ink-2 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/15 disabled:opacity-60"
                        >
                          <option value="ask">Ask</option>
                          <option value="full_access">Full access</option>
                        </select>
                      </label>
                      <span class="${() => composerStatusDotClass(state)}" aria-hidden="true"></span>
                      <span class="font-mono text-[11px] uppercase tracking-wider text-ink-3 truncate">
                        ${() => composerStatusLabel(state)}
                      </span>
                    </div>
                    <div class="flex items-center gap-2">
                      ${() => state.isStreaming
                        ? html`<button
                            type="button"
                            @click="${() => callbacksRef.current.onStop()}"
                            class="inline-flex items-center gap-2 rounded-full border border-warm-border bg-paper px-4 py-2 font-mono text-[11px] uppercase tracking-wider font-semibold text-ink-2 hover:border-ink-3 hover:text-ink transition duration-150 active:scale-95 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/15"
                            aria-label="Stop"
                          >stop</button>`
                        : null}
                      <button
                        type="submit"
                        disabled="${() => !state.draft.trim() || !state.tenantId || state.isStreaming}"
                        class="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 font-mono text-[11px] uppercase tracking-wider font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 transition duration-150 enabled:active:scale-95 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-coral/15"
                        aria-label="Send"
                      >
                        ${() => state.isStreaming ? 'sending' : 'send'}
                        ${() => state.isStreaming
                          ? null
                          : html`<kbd class="rounded bg-primary-foreground/15 px-1 py-0.5 text-[10px] text-primary-foreground/80">⏎</kbd>`}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    ` : null}`;

    view(mount);
    const mountedMarkdown = mountedMarkdownRef.current;

    return () => {
      state.mounted = false;
      for (const mounted of mountedMarkdown.values()) {
        mounted.root.unmount();
      }
      mountedMarkdown.clear();
      stateRef.current = null;
      mount.replaceChildren();
    };
    // Mount once; props sync through the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const state = stateRef.current;
    if (!state) return;
    state.tenantId = tenantId || '';
    state.contextLabel = contextLabel;
    state.draft = draft;
    state.status = status;
    if (state.isStreaming !== isStreaming) {
      // A new turn cuts the last celebration short; a finished reply that earns it starts one.
      state.happy = !isStreaming && replyCelebrates(messages);
    }
    state.isStreaming = isStreaming;
    state.conversationLoading = conversationLoading;
    state.activeConversationId = activeConversationId || '';
    state.chatAccessMode = chatAccessMode;
    state.messages = messages;
    state.conversations = conversations;
    state.toolActivity = toolActivity;
    state.turnStartedAt = turnStartedAt ?? 0;
    state.modes = modes;
    state.workflowTiles = workflowTiles;
    state.helpText = helpText;
  }, [
    activeConversationId,
    chatAccessMode,
    contextLabel,
    conversationLoading,
    conversations,
    draft,
    helpText,
    isStreaming,
    messages,
    modes,
    status,
    tenantId,
    toolActivity,
    turnStartedAt,
    workflowTiles,
  ]);

  // The turn's wall clock. It ticks into the arrow state rather than into React
  // state so a running turn repaints one text node instead of re-rendering the
  // whole surface ten times a second. Tenths, because the readout shows tenths.
  useEffect(() => {
    const settle = (value: number) => {
      const state = stateRef.current;
      if (state) state.elapsedMs = value;
    };
    if (!turnStartedAt) {
      settle(0);
      return;
    }
    const state = stateRef.current;
    if (state && 'live' in state.traceOpen) {
      const rest = { ...state.traceOpen };
      delete rest.live;
      state.traceOpen = rest;
    }
    settle(Date.now() - turnStartedAt);
    const timer = setInterval(() => settle(Date.now() - turnStartedAt), 100);
    return () => clearInterval(timer);
  }, [turnStartedAt]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;

    void nextTick(() => {
      if (cancelled || !mount.isConnected) return;

      const assistantMessages = new Map(
        messages
          .filter((message) => message.role === 'assistant')
          .map((message) => [message.id, message]),
      );

      for (const messageId of Array.from(mountedMarkdownRef.current.keys())) {
        if (!assistantMessages.has(messageId)) {
          const mounted = mountedMarkdownRef.current.get(messageId);
          if (mounted) {
            mounted.root.unmount();
            mountedMarkdownRef.current.delete(messageId);
          }
        }
      }

      for (const message of assistantMessages.values()) {
        const element = Array.from(
          mount.querySelectorAll('[data-central-chat-message-md]'),
        ).find((candidate) => candidate.getAttribute('data-central-chat-message-md') === message.id);
        if (!element) continue;

        const content = message.content || '';
        const current = mountedMarkdownRef.current.get(message.id);

        if (current && current.element !== element) {
          current.root.unmount();
          mountedMarkdownRef.current.delete(message.id);
        }

        const fresh = mountedMarkdownRef.current.get(message.id);
        if (!fresh) {
          const root = createRoot(element as HTMLElement);
          root.render(<MarkdownContent content={content} />);
          mountedMarkdownRef.current.set(message.id, { content, root, element });
        } else if (fresh.content !== content) {
          fresh.root.render(<MarkdownContent content={content} />);
          fresh.content = content;
        }
      }

      mount
        .querySelector('[data-central-chat-scroll-anchor]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    });

    return () => {
      cancelled = true;
    };
  }, [messages]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;

    void nextTick(() => {
      if (cancelled || !mount.isConnected) return;

      const proposalMessages = new Map(
        messages
          .filter((message) => message.taProposal)
          .map((message) => [message.id, message]),
      );

      for (const messageId of Array.from(mountedTaProposalRef.current.keys())) {
        if (!proposalMessages.has(messageId)) {
          const mounted = mountedTaProposalRef.current.get(messageId);
          if (mounted) {
            mounted.root.unmount();
            mountedTaProposalRef.current.delete(messageId);
          }
        }
      }

      for (const message of proposalMessages.values()) {
        if (!message.taProposal) continue;
        const element = Array.from(
          mount.querySelectorAll('[data-central-chat-ta-proposal]'),
        ).find((candidate) => candidate.getAttribute('data-central-chat-ta-proposal') === message.id);
        if (!element) continue;

        const current = mountedTaProposalRef.current.get(message.id);
        if (current && current.element !== element) {
          current.root.unmount();
          mountedTaProposalRef.current.delete(message.id);
        }

        const fresh = mountedTaProposalRef.current.get(message.id);
        if (!fresh) {
          const root = createRoot(element as HTMLElement);
          root.render(
            <TechnicalAuditProposalCard
              key={message.taProposal.proposalId}
              proposal={message.taProposal}
              startPhase={message.taProposalRestored?.phase}
              startReceipt={message.taProposalRestored?.receipt}
            />,
          );
          mountedTaProposalRef.current.set(message.id, {
            proposalId: message.taProposal.proposalId,
            root,
            element,
          });
        } else if (fresh.proposalId !== message.taProposal.proposalId) {
          fresh.root.render(
            <TechnicalAuditProposalCard
              key={message.taProposal.proposalId}
              proposal={message.taProposal}
              startPhase={message.taProposalRestored?.phase}
              startReceipt={message.taProposalRestored?.receipt}
            />,
          );
          fresh.proposalId = message.taProposal.proposalId;
        }
      }
    });

    return () => {
      cancelled = true;
    };
  }, [messages]);

  return <div ref={mountRef} className={className} />;
}
